import React, { useEffect, useState, useCallback } from "react";
import { Navbar, Dropdown, Container, Image, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import api from "../api";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeadset, faBell } from "@fortawesome/free-solid-svg-icons";
import { supportApi } from "../services/supportApi";
import { socialNotificationApi } from "../services/socialNotificationApi";
import { FiSend } from "react-icons/fi";
import AppSwitcher from "./share/AppSwitcher";
import { SHARE_URL } from "../utils/appHost";

const SUPERADMIN_EMAILS = ["johann.t.events@gmail.com", "thuillierjohann@gmail.com"];

const HeaderAdmin = ({ page_name, setEvent, onLogout, user, isAuthenticated }) => {
  const { t } = useTranslation();
  const { joinedEvents } = useAuth();
  const [showNavBar, setShowNavBar] = useState(false);
  const companyName = user?.company_name || user?.firstname;
  const { id_name } = useParams();
  const showPageName = page_name && !["login", "checkout", "pricing plans"].includes(page_name.toLowerCase());

  const fetchEventName = async () => {
    if (!id_name || !setEvent) return;
    try {
      const response = await api.get(
        process.env.REACT_APP_MODEL_ENDPOINT + `/check_eid?id_name=${id_name}`
      );
      if (response.data && response.data.success) {
        setEvent(response.data.event);
        api.post(`click/${response.data.event.event_id}`);
      } else {
        setEvent(false);
      }
    } catch {
      setEvent(false);
    }
  };

  useEffect(() => {
    fetchEventName();
  }, [id_name]);

  useEffect(() => {
    setShowNavBar(isAuthenticated);
  }, [isAuthenticated]);

  const isSuperAdmin = SUPERADMIN_EMAILS.includes(user?.email);

  const [supportUnread, setSupportUnread] = useState(0);
  const [socialUnread, setSocialUnread] = useState({ total: 0, perEvent: {} });

  const fetchSupportUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = isSuperAdmin
        ? await supportApi.adminGetUnreadCount()
        : await supportApi.getUnreadCount();
      setSupportUnread(res.data.total || 0);
    } catch {}
  }, [isAuthenticated, isSuperAdmin]);

  const fetchSocialUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await socialNotificationApi.getUnreadCount();
      setSocialUnread({
        total: res.data.total || 0,
        perEvent: res.data.per_event || {},
      });
    } catch {}
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSupportUnread();
    const id = setInterval(fetchSupportUnread, 60000);
    return () => clearInterval(id);
  }, [fetchSupportUnread]);

  useEffect(() => {
    fetchSocialUnread();
    const id = setInterval(fetchSocialUnread, 60000);
    return () => clearInterval(id);
  }, [fetchSocialUnread]);

  const totalUnread = supportUnread + socialUnread.total;

  const initials = companyName
    ? companyName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <>
      <Navbar
        expand="lg"
        className="border-bottom py-0"
        style={{ background: "#fff", minHeight: "56px" }}
        dir="ltr"
      >
        <Container fluid className="px-3 px-md-4">
          {/* Left: logo — hidden on desktop (sidebar shows logo), visible on mobile */}
          <Navbar.Brand href="/admin" className="me-auto py-2 d-md-none">
            <Image
              src={`${process.env.REACT_APP_MODEL_ENDPOINT}/user_logo?user_id=${user?.user_id}&t=${Date.now()}`}
              alt={t("header_admin.logo_alt")}
              height="36"
              style={{ borderRadius: "6px", objectFit: "contain" }}
            />
          </Navbar.Brand>

          {/* Center: page name */}
          {showPageName && (
            <span
              className="position-absolute start-50 translate-middle-x d-none d-md-block"
              style={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#6c757d",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {page_name}
            </span>
          )}

          {/* Right: notification bell + avatar */}
          {showNavBar && (
            <div className="d-flex align-items-center gap-2 ms-auto">

              {/* ── Switch to Pic-Loud Share (file transfers) ── */}
              <AppSwitcher current="main" />

              {/* ── Notification bell ── */}
              <Dropdown align="end">
                <Dropdown.Toggle
                  as="div"
                  style={{ cursor: "pointer" }}
                  className="d-flex align-items-center"
                >
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: "#f3f4f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#4b5563",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#e5e7eb")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                    >
                      <FontAwesomeIcon icon={faBell} style={{ fontSize: "15px" }} />
                    </div>
                    {totalUnread > 0 && (
                      <Badge
                        bg="danger"
                        pill
                        style={{
                          position: "absolute",
                          top: -3,
                          right: -3,
                          fontSize: "0.58rem",
                          minWidth: 15,
                          height: 15,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 3px",
                          lineHeight: 1,
                        }}
                      >
                        {totalUnread}
                      </Badge>
                    )}
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu
                  className="mt-2 border-0 shadow"
                  style={{ minWidth: "200px", borderRadius: "10px", padding: "6px" }}
                >
                  {/* Support item */}
                  <Dropdown.Item
                    href="/support"
                    className="rounded px-3 py-2 d-flex align-items-center justify-content-between"
                    style={{ fontSize: "0.88rem" }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faHeadset} className="me-2 text-muted" />
                      {t("support.menu_item")}
                    </span>
                    {supportUnread > 0 && (
                      <Badge bg="danger" pill style={{ fontSize: "0.65rem" }}>
                        {supportUnread}
                      </Badge>
                    )}
                  </Dropdown.Item>

                  {/* Joined events */}
                  {joinedEvents?.length > 0 && (() => {
                    const sorted = [...joinedEvents].sort((a, b) => {
                      const aCnt = socialUnread.perEvent[a.event_id] || 0;
                      const bCnt = socialUnread.perEvent[b.event_id] || 0;
                      if (bCnt !== aCnt) return bCnt - aCnt;
                      return new Date(b.date ?? 0) - new Date(a.date ?? 0);
                    });
                    return (
                      <>
                        <Dropdown.Divider className="my-1" />
                        <Dropdown.Header style={{ fontSize: "0.72rem", color: "#adb5bd", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Joined Events
                        </Dropdown.Header>
                        {sorted.slice(0, 5).map((ev) => {
                          const evCount = socialUnread.perEvent[ev.event_id] || 0;
                          return (
                            <Dropdown.Item
                              key={ev.event_id}
                              href={`/event/${ev.id_name}/social`}
                              className="rounded px-3 py-2 d-flex align-items-center justify-content-between"
                              style={{ fontSize: "0.88rem" }}
                              onClick={() => {
                                if (evCount > 0) {
                                  socialNotificationApi.markRead(ev.event_id).catch(() => {});
                                  setSocialUnread((prev) => {
                                    const next = { ...prev.perEvent };
                                    delete next[ev.event_id];
                                    return { total: Math.max(0, prev.total - evCount), perEvent: next };
                                  });
                                }
                              }}
                            >
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {ev.name}
                              </span>
                              {evCount > 0 && (
                                <Badge bg="danger" pill className="ms-2 flex-shrink-0" style={{ fontSize: "0.6rem", minWidth: 15 }}>
                                  {evCount}
                                </Badge>
                              )}
                            </Dropdown.Item>
                          );
                        })}
                        {joinedEvents.length > 5 && (
                          <Dropdown.Item href="/social-events" className="rounded px-3 py-2" style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                            See All ({joinedEvents.length})
                          </Dropdown.Item>
                        )}
                      </>
                    );
                  })()}

                  {/* Superadmin support */}
                  {isSuperAdmin && (
                    <>
                      <Dropdown.Divider className="my-1" />
                      <Dropdown.Item
                        href="/admin/support"
                        className="rounded px-3 py-2 d-flex align-items-center justify-content-between"
                        style={{ fontSize: "0.88rem" }}
                      >
                        <span>{t("support.admin_title")}</span>
                        {supportUnread > 0 && (
                          <Badge bg="danger" pill style={{ fontSize: "0.65rem" }}>
                            {supportUnread}
                          </Badge>
                        )}
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>

              {/* ── Avatar / account dropdown ── */}
              <Dropdown align="end">
                <Dropdown.Toggle
                  as="div"
                  id="dropdown-account"
                  style={{ cursor: "pointer" }}
                  className="d-flex align-items-center gap-2"
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #a3a6ab, #7d8085)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      userSelect: "none",
                    }}
                  >
                    {initials}
                  </div>
                  <span className="d-none d-sm-block fw-semibold text-dark" style={{ fontSize: "0.88rem" }}>
                    {companyName}
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu
                  className="mt-2 border-0 shadow"
                  style={{ minWidth: "180px", borderRadius: "10px", padding: "6px" }}
                >
                  <Dropdown.Item href={`${SHARE_URL}/`} className="rounded px-3 py-2" style={{ fontSize: "0.88rem" }}>
                    <FiSend className="me-2 text-muted" />
                    {t("header_admin.file_transfers")}
                  </Dropdown.Item>
                  <Dropdown.Item href="/admin/settings" className="rounded px-3 py-2" style={{ fontSize: "0.88rem" }}>
                    {t("header_admin.settings")}
                  </Dropdown.Item>
                  {isSuperAdmin && (
                    <>
                      <Dropdown.Divider className="my-1" />
                      <Dropdown.Item href="/admin/dashboard" className="rounded px-3 py-2" style={{ fontSize: "0.88rem" }}>
                        {t("header_admin.dashboard")}
                      </Dropdown.Item>
                    </>
                  )}
                  <Dropdown.Divider className="my-1" />
                  <Dropdown.Item onClick={onLogout} className="rounded px-3 py-2 text-danger" style={{ fontSize: "0.88rem" }}>
                    {t("header_admin.logout")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

            </div>
          )}
        </Container>
      </Navbar>

      {showPageName && (
        <div className="d-md-none px-3 pt-2 pb-1" dir="ltr">
          <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#6c757d", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            {page_name}
          </span>
        </div>
      )}
    </>
  );
};

export default HeaderAdmin;
